import { useEffect } from "react";

interface SEOMetaProps {
  title: string;
  description: string;
  keywords?: string;
  canonicalUrl?: string;
  ogImage?: string;
  ogType?: string;
  noindex?: boolean;
  schemaData?: Record<string, unknown>;
}

const DEFAULT_OG_IMAGE =
  "https://storage.readdy-site.link/project_files/4e9a12fb-cddb-4082-86d9-a8d48c5e1af1/4c1dbfd3-51c3-4b4d-bce2-d21c130722c0_LOGO---BHODEV.jpeg";

/**
 * Sets SEO meta tags dynamically for any page.
 * Call this in the top-level of a page component inside useEffect.
 */
export function useSEOMeta({
  title,
  description,
  keywords,
  canonicalUrl,
  ogImage,
  ogType = "website",
  noindex,
  schemaData,
}: SEOMetaProps) {
  const setMeta = (selector: string, content: string, attr = "name") => {
    let el = document.querySelector(`meta[${attr}="${selector}"]`) as HTMLMetaElement | null;
    if (!el) {
      el = document.createElement("meta");
      el.setAttribute(attr, selector);
      document.head.appendChild(el);
    }
    el.setAttribute("content", content);
  };

  const removeMeta = (selector: string, attr = "name") => {
    const el = document.querySelector(`meta[${attr}="${selector}"]`);
    if (el) el.remove();
  };

  const setLink = (rel: string, href: string) => {
    let el = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null;
    if (!el) {
      el = document.createElement("link");
      el.setAttribute("rel", rel);
      document.head.appendChild(el);
    }
    el.setAttribute("href", href);
  };

  const removeLink = (rel: string) => {
    const el = document.querySelector(`link[rel="${rel}"]`);
    if (el) el.remove();
  };

  const removeSchema = () => {
    const el = document.querySelector('script[type="application/ld+json"]');
    if (el) el.remove();
  };

  useEffect(() => {
    const baseTitle =
      "BHODEV Cameroon - Bogi Health Organization for Development | Community Health NGO";
    document.title = title ? `${title} | BHODEV` : baseTitle;

    const ogTitle = title ? `${title} | BHODEV` : baseTitle;
    const resolvedOgImage = ogImage || DEFAULT_OG_IMAGE;

    setMeta("description", description);
    if (keywords) setMeta("keywords", keywords);
    else removeMeta("keywords");

    if (noindex) {
      setMeta("robots", "noindex, nofollow");
    } else {
      removeMeta("robots");
    }

    setMeta("og:title", ogTitle, "property");
    setMeta("og:description", description, "property");
    setMeta("og:type", ogType, "property");
    setMeta("og:image", resolvedOgImage, "property");
    setMeta("og:url", canonicalUrl || window.location.href, "property");

    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", ogTitle);
    setMeta("twitter:description", description);
    setMeta("twitter:image", resolvedOgImage);

    if (canonicalUrl) setLink("canonical", canonicalUrl);
    else removeLink("canonical");

    removeSchema();
    if (schemaData) {
      const script = document.createElement("script");
      script.type = "application/ld+json";
      script.text = JSON.stringify(schemaData);
      document.head.appendChild(script);
    }

    return () => {
      removeSchema();
    };
  }, [title, description, keywords, canonicalUrl, ogImage, ogType, noindex, schemaData]);
}

/**
 * Shared structured data generators
 */
export function generateOrganizationSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "NGO",
    name: "Bogi Health Organization for Development (BHODEV)",
    alternateName: "BHODEV Cameroon",
    url: "https://bhodevcameroon.org",
    logo: "https://storage.readdy-site.link/project_files/4e9a12fb-cddb-4082-86d9-a8d48c5e1af1/4c1dbfd3-51c3-4b4d-bce2-d21c130722c0_LOGO---BHODEV.jpeg",
    description:
      "BHODEV is an NGO working to promote health, peace, social justice and sustainable development in Cameroon since 2014.",
    foundingDate: "2014",
    areaServed: {
      "@type": "Place",
      name: "Cameroon",
      address: {
        "@type": "PostalAddress",
        addressLocality: "Buea",
        addressRegion: "South West Region",
        addressCountry: "CM",
      },
    },
    address: {
      "@type": "PostalAddress",
      streetAddress: "Clerks quarters opposite express exchange",
      addressLocality: "Buea",
      addressRegion: "South West Region",
      addressCountry: "CM",
    },
    contactPoint: {
      "@type": "ContactPoint",
      telephone: "+237-670-396-340",
      contactType: "General Inquiries",
      email: "info@bhodevcameroon.org",
    },
    sameAs: [
      "https://web.facebook.com/profile.php?id=100094000555791",
      "https://www.linkedin.com/in/bogi-health-organization-for-development-bhodev-805471276/",
      "https://twitter.com/bhodevCameroon",
      "https://www.instagram.com/bhodevcameroon_ngo/",
      "https://www.youtube.com/@bhodevcameroon",
    ],
    potentialAction: {
      "@type": "DonateAction",
      target: {
        "@type": "EntryPoint",
        urlTemplate: "https://bhodevcameroon.org/#donate",
      },
      name: "Donate to BHODEV",
    },
  };
}

export function generateWebPageSchema(title: string, description: string, url: string) {
  return {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: title,
    description,
    url,
    publisher: {
      "@type": "Organization",
      name: "BHODEV Cameroon",
      logo: {
        "@type": "ImageObject",
        url: "https://storage.readdy-site.link/project_files/4e9a12fb-cddb-4082-86d9-a8d48c5e1af1/4c1dbfd3-51c3-4b4d-bce2-d21c130722c0_LOGO---BHODEV.jpeg",
      },
    },
  };
}

export function generateArticleSchema(
  title: string,
  description: string,
  url: string,
  image: string,
  datePublished: string,
  authorName: string,
  category?: string
) {
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: title,
    description,
    image,
    url,
    datePublished,
    author: {
      "@type": "Person",
      name: authorName,
    },
    publisher: {
      "@type": "Organization",
      name: "BHODEV Cameroon",
      logo: {
        "@type": "ImageObject",
        url: "https://storage.readdy-site.link/project_files/4e9a12fb-cddb-4082-86d9-a8d48c5e1af1/4c1dbfd3-51c3-4b4d-bce2-d21c130722c0_LOGO---BHODEV.jpeg",
      },
    },
    articleSection: category || "News",
    inLanguage: "en",
    isPartOf: {
      "@type": "WebSite",
      name: "BHODEV Cameroon",
      url: "https://bhodevcameroon.org",
    },
  };
}

export function generateBreadcrumbSchema(items: { name: string; url: string }[]) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: item.name,
      item: item.url,
    })),
  };
}

export function generateFAQPageSchema(faqs: { question: string; answer: string }[]) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.answer,
      },
    })),
  };
}

export function generatePersonSchema(
  name: string,
  role: string,
  description: string,
  image?: string,
  sameAs?: string[]
) {
  return {
    "@context": "https://schema.org",
    "@type": "Person",
    name,
    jobTitle: role,
    description,
    image,
    sameAs,
    worksFor: {
      "@type": "Organization",
      name: "BHODEV Cameroon",
      url: "https://bhodevcameroon.org",
    },
  };
}

export function generateLocalBusinessSchema(
  name: string,
  description: string,
  url: string,
  image: string,
  telephone: string,
  email: string,
  streetAddress: string,
  city: string,
  region: string,
  country: string,
  latitude: number,
  longitude: number,
  hours: { dayOfWeek: string; opens: string; closes: string }[],
  priceRange?: string
) {
  const schema: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    name,
    description,
    url,
    image,
    telephone,
    email,
    address: {
      "@type": "PostalAddress",
      streetAddress,
      addressLocality: city,
      addressRegion: region,
      addressCountry: country,
    },
    geo: {
      "@type": "GeoCoordinates",
      latitude,
      longitude,
    },
    openingHoursSpecification: hours.map((h) => ({
      "@type": "OpeningHoursSpecification",
      dayOfWeek: h.dayOfWeek,
      opens: h.opens,
      closes: h.closes,
    })),
  };
  if (priceRange) {
    schema.priceRange = priceRange;
  }
  return schema;
}